import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SellJunkCarPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    vin: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    condition: 'running',
    location: '',
    zip_code: '',
    seller_name: '',
    seller_email: '',
    seller_phone: '',
    description: '',
    asking_price: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        year: parseInt(formData.year),
        asking_price: formData.asking_price ? parseFloat(formData.asking_price) : null
      };

      await axios.post(`${API_URL}/api/junk-leads`, submitData);
      setSubmitted(true);
      toast.success('Your vehicle has been listed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-8">
        <div className="w-full max-w-lg text-center bg-white rounded-md p-12 border border-[rgba(10,10,10,0.1)]">
          <div className="w-20 h-20 bg-[#16A34A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#16A34A]" />
          </div>
          <h1 
            className="text-3xl font-bold text-[#0A0A0A] mb-4 tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            data-testid="success-title"
          >
            Submission Received!
          </h1>
          <p className="text-[#52525B] mb-8">
            Your junk car listing has been submitted. Buyers in your area will receive your lead and may contact you with offers within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF] hover:text-white"
            >
              Back to Home
            </Button>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  vin: '', make: '', model: '', year: new Date().getFullYear(),
                  condition: 'running', location: '', zip_code: '',
                  seller_name: '', seller_email: '', seller_phone: '',
                  description: '', asking_price: ''
                });
              }}
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
            >
              Submit Another Vehicle
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="sell-junk-logo">
              <img 
                src="/wakzy-logo.png" 
                alt="Wakzy Auto Tech" 
                className="h-12 w-auto"
              />
            </Link>
            
            <Link to="/" className="text-[#52525B] hover:text-[#0A0A0A] transition-colors font-medium">
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-12 bg-[#0A0A0A] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="label-caps text-[#0052FF] mb-4 block">JUNK CAR MARKETPLACE</span>
          <h1 
            className="text-4xl sm:text-5xl font-black leading-none tracking-tight mb-6"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            data-testid="sell-junk-hero-title"
          >
            Get Cash for Your Junk Car
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Submit your vehicle details and receive competitive offers from local scrap yards, recyclers, and salvage buyers within 24 hours.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-md border border-[rgba(10,10,10,0.1)] p-8 shadow-sm">
            <h2 
              className="text-2xl font-bold text-[#0A0A0A] mb-6 tracking-tight"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Vehicle Information
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="make" className="text-[#0A0A0A] font-medium">Make *</Label>
                  <Input
                    id="make"
                    placeholder="e.g., Toyota, Honda, Ford"
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    className="mt-2 h-12 border-[rgba(10,10,10,0.1)]"
                    required
                    data-testid="junk-make-input"
                  />
                </div>
                <div>
                  <Label htmlFor="model" className="text-[#0A0A0A] font-medium">Model *</Label>
                  <Input
                    id="model"
                    placeholder="e.g., Camry, Civic, F-150"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="mt-2 h-12 border-[rgba(10,10,10,0.1)]"
                    required
                    data-testid="junk-model-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="year" className="text-[#0A0A0A] font-medium">Year *</Label>
                  <Input
                    id="year"
                    type="number"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    className="mt-2 h-12 border-[rgba(10,10,10,0.1)]"
                    required
                    data-testid="junk-year-input"
                  />
                </div>
                <div>
                  <Label htmlFor="vin" className="text-[#0A0A0A] font-medium">VIN (Optional)</Label>
                  <Input
                    id="vin"
                    placeholder="17-character VIN"
                    value={formData.vin}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
                    className="mt-2 h-12 border-[rgba(10,10,10,0.1)]"
                    maxLength={17}
                    data-testid="junk-vin-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="condition" className="text-[#0A0A0A] font-medium">Condition *</Label>
                  <Select 
                    value={formData.condition} 
                    onValueChange={(value) => setFormData({ ...formData, condition: value })}
                  >
                    <SelectTrigger className="mt-2 h-12 border-[rgba(10,10,10,0.1)]" data-testid="junk-condition-select">
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="running">Running - Drives fine</SelectItem>
                      <SelectItem value="not_running">Not Running - Doesn't start</SelectItem>
                      <SelectItem value="damaged">Damaged - Accident damage</SelectItem>
                      <SelectItem value="totaled">Totaled - Total loss</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="asking_price" className="text-[#0A0A0A] font-medium">Asking Price (Optional)</Label>
                  <Input
                    id="asking_price"
                    type="number"
                    placeholder="$"
                    value={formData.asking_price}
                    onChange={(e) => setFormData({ ...formData, asking_price: e.target.value })}
                    className="mt-2 h-12 border-[rgba(10,10,10,0.1)]"
                    data-testid="junk-price-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="location" className="text-[#0A0A0A] font-medium">City/Location *</Label>
                  <Input
                    id="location"
                    placeholder="e.g., Austin, TX"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="mt-2 h-12 border-[rgba(10,10,10,0.1)]"
                    required
                    data-testid="junk-location-input"
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code" className="text-[#0A0A0A] font-medium">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    placeholder="e.g., 78701"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    className="mt-2 h-12 border-[rgba(10,10,10,0.1)]"
                    required
                    maxLength={10}
                    data-testid="junk-zip-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="text-[#0A0A0A] font-medium">Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Tell us more about the vehicle's condition, any issues, damage details, etc."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-2 border-[rgba(10,10,10,0.1)] min-h-[100px]"
                  data-testid="junk-description-input"
                />
              </div>

              <hr className="border-[rgba(10,10,10,0.1)]" />

              <h3 
                className="text-xl font-bold text-[#0A0A0A] tracking-tight"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                Your Contact Information
              </h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="seller_name" className="text-[#0A0A0A] font-medium">Your Name *</Label>
                  <Input
                    id="seller_name"
                    placeholder="Full name"
                    value={formData.seller_name}
                    onChange={(e) => setFormData({ ...formData, seller_name: e.target.value })}
                    className="mt-2 h-12 border-[rgba(10,10,10,0.1)]"
                    required
                    data-testid="junk-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="seller_phone" className="text-[#0A0A0A] font-medium">Phone Number *</Label>
                  <Input
                    id="seller_phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.seller_phone}
                    onChange={(e) => setFormData({ ...formData, seller_phone: e.target.value })}
                    className="mt-2 h-12 border-[rgba(10,10,10,0.1)]"
                    required
                    data-testid="junk-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="seller_email" className="text-[#0A0A0A] font-medium">Email *</Label>
                <Input
                  id="seller_email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.seller_email}
                  onChange={(e) => setFormData({ ...formData, seller_email: e.target.value })}
                  className="mt-2 h-12 border-[rgba(10,10,10,0.1)]"
                  required
                  data-testid="junk-email-input"
                />
              </div>

              <div className="bg-[#F4F4F5] rounded-md p-4 text-sm text-[#52525B]">
                By submitting, you agree that your contact information will be shared with verified buyers in your area.
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#0052FF] hover:bg-[#0043D1] text-white font-semibold text-lg"
                data-testid="junk-submit-btn"
              >
                {loading ? 'Submitting...' : 'Submit & Get Offers'} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-[#F4F4F5]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 
            className="text-3xl font-bold text-center text-[#0A0A0A] mb-12 tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: "01", title: "Submit Details", desc: "Fill out the form with your vehicle information" },
              { num: "02", title: "Receive Offers", desc: "Get competitive offers from local buyers within 24 hours" },
              { num: "03", title: "Get Paid", desc: "Accept the best offer and schedule pickup" }
            ].map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="text-5xl font-black text-[#0052FF]/20 mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">{step.title}</h3>
                <p className="text-[#52525B]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
